import React, { useEffect, useRef, useState } from "react";
import { Drawer, Button, message, Select } from "antd";
import {
  Save,
  Trash2,
  Undo2,
  Layers,
  Coins,
  Eye,
  ReceiptText,
  FileText,
  Wrench,
} from "lucide-react";
import { CustomerModal, drawerTitle } from "../../../components";
import { useCart } from "../../../Context";
import { useSectionAccess } from "../../../Services";
import { PosAction } from "../../../Types";
import { DraftOptions, DraftOptionsRef } from "./DraftOptions";
import { CashFlowModal } from "./CashFlowModal";
import { TableModal } from "./shortcut-panel/TableModal";

interface QuickActionProps {
  open: boolean;
  onClose: () => void;
  onOpenReturn: () => void;
  onOpenExpenses: () => void;
  customers: any[];
  tables: any[];
  loadingCustomers: boolean;
  loadingTables: boolean;
  refetchTables: () => void;
  onSaveQuotationClick: () => void;
  onOpenServices: () => void;
  isRestaurant: boolean;
  isService: boolean;
}

export const QuickAction: React.FC<QuickActionProps> = ({
  open,
  onClose,
  onOpenReturn,
  onOpenExpenses,
  customers,
  tables,
  loadingCustomers,
  refetchTables,
  onSaveQuotationClick,
  onOpenServices,
  isRestaurant,
  isService,
}) => {
  const selectRef = useRef<any>(null);
  const draftOptionsRef = useRef<DraftOptionsRef>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isCashFlowOpen, setIsCashFlowOpen] = useState(false);
  const { isAllowed } = useSectionAccess("pos");

  const {
    customer,
    setCustomer,
    table,
    setTable,
    clearCart,
    isWholeSale,
    setIsWholeSale,
    billingLayout,
    setBillingLayout,
    billType,
    setBillType,
    cart,
  } = useCart();

  const handleClearCart = () => {
    clearCart();
    message.success("Current cart cleared successfully.");
    onClose();
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        selectRef.current?.focus();
      }, 50);
    }
  }, [open]);

  return (
    <>
      <Drawer
        title={drawerTitle("Quick Action")}
        placement="right"
        onClose={onClose}
        open={open}
        width={500}
      >
        <div className="flex flex-col mb-16 gap-y-5 h-full">
          <div className="flex flex-col gap-2 mb-3 w-full">
            <div className="flex items-center gap-x-1 justify-between w-full">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Customer
              </span>
              <Button
                type="link"
                size="small"
                className="p-0 h-auto text-xs font-semibold"
                onClick={() => setIsCustomerModalOpen(true)}
              >
                (Add New)
              </Button>
            </div>
            <Select
              allowClear
              ref={selectRef}
              showSearch
              placeholder="Select Customer"
              className="flex-grow border-gray-300 rounded-md"
              size="middle"
              value={customer?.id || undefined}
              onChange={(val) => {
                const selected = customers.find((c: any) => c.id === val);
                if (selected) {
                  setCustomer({
                    id: selected.id,
                    name: selected.name,
                    credit_limit: selected?.credit_limit,
                  });
                } else {
                  setCustomer(null);
                }
              }}
              loading={loadingCustomers}
              optionFilterProp="label"
              options={customers?.map((c) => ({
                value: c.id,
                label: `${c.name} (${c.phone})`,
              }))}
            />
          </div>

          {isRestaurant && (
            <div className="flex flex-col gap-2 mb-3 w-full">
              <div className="flex items-center gap-x-1 justify-between w-full">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Table
                </span>
                <Button
                  type="link"
                  size="small"
                  className="p-0 h-auto text-xs font-semibold float-end item-end"
                  onClick={() => setIsTableModalOpen(true)}
                >
                  (Add New)
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {tables.map((t: any) => (
                  <Button
                    key={t.id}
                    disabled={t.current_status !== "available"}
                    onClick={() => {
                      setTable({ id: t.id, name: t.name });
                      onClose();
                    }}
                    className={`w-20 h-20 border rounded-md flex items-center cursor-pointer justify-center text-center
                  ${
                    t.current_status === "available"
                      ? "border-green-500 bg-green-50"
                      : "border-red-500 bg-red-50"
                  }`}
                  >
                    {t.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <Button
            size="large"
            icon={<Coins className="w-4 h-4" />}
            className="flex items-center justify-center gap-2 h-11 text-xs font-semibold tracking-wide border-blue-600 text-blue-600 hover:border-blue-700 hover:text-blue-700"
            onClick={() => {
              setIsCashFlowOpen(true);
              onClose();
            }}
          >
            CASH REGISTER
          </Button>

          <div className="grid grid-cols-3 gap-2">
            <Button
              key={"SAVE"}
              size="large"
              className="flex flex-col items-center justify-center gap-1 h-[72px] text-[10px] font-semibold tracking-wide border-red-300 text-red-500 hover:bg-red-50"
              onClick={() => draftOptionsRef.current?.handleSaveDraft()}
              loading={isSavingDraft}
              disabled={cart.length === 0}
              danger
            >
              <Save className="w-5 h-5 mb-0.5" />
              SAVE DRAFT
            </Button>

            <Button
              key={"VIEW_DRAFTS"}
              size="large"
              className="flex flex-col items-center justify-center gap-1 h-[72px] text-[10px] font-semibold tracking-wide border-emerald-300 text-emerald-600 hover:bg-emerald-50"
              onClick={() => draftOptionsRef.current?.openDraftList()}
            >
              <Eye className="w-5 h-5 mb-0.5" />
              VIEW DRAFTS
            </Button>

            {isAllowed(PosAction.VoidTransaction) ? (
              <Button
                key={"CLEAR"}
                size="large"
                className="flex flex-col items-center justify-center gap-1 h-[72px] text-[10px] font-semibold tracking-wide border-blue-300 text-blue-600 hover:bg-blue-50"
                onClick={handleClearCart}
              >
                <Trash2 className="w-5 h-5 mb-0.5" />
                CLEAR CART
              </Button>
            ) : (
              <div />
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mt-1">
            <Button
              key={"SAVE_QUOTATION"}
              size="large"
              className="flex flex-col items-center justify-center gap-1 h-[72px] text-[10px] font-semibold tracking-wide border-red-300 text-red-500 hover:bg-red-50"
              onClick={onSaveQuotationClick}
              disabled={cart.length === 0}
            >
              <FileText className="w-5 h-5 mb-0.5" />
              SAVE QUOTATION
            </Button>

            {isService && (
              <Button
                key={"SERVICES"}
                size="large"
                className="flex flex-col items-center justify-center gap-1 h-[72px] text-[10px] font-semibold tracking-wide border-amber-300 text-amber-600 hover:bg-amber-50"
                onClick={() => {
                  onClose();
                  onOpenServices();
                }}
              >
                <Wrench className="w-5 h-5 mb-0.5" />
                SERVICES
              </Button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button
              key="WHOLESALE"
              size="large"
              className={`flex flex-col items-center justify-center gap-1.5 h-[72px] text-xs font-semibold tracking-wide rounded-md text-sm font-medium transition-all ${
                isWholeSale
                  ? "bg-amber-500 hover:bg-amber-600 text-white border-none"
                  : "border-gray-900 text-gray-800"
              }`}
              onClick={() => {
                setIsWholeSale(!isWholeSale);
                message.success(
                  isWholeSale
                    ? "Switched to Retail Price"
                    : "Switched to Wholesale Price",
                );
                onClose();
              }}
            >
              <Layers className="w-5 h-5" />
              {isWholeSale ? "RETAIL PRICE" : "WHOLESALE"}
            </Button>

            <Button
              key="RETURN"
              size="large"
              className="flex flex-col items-center justify-center gap-1.5 h-[72px] text-xs font-semibold tracking-wide border-gray-900 rounded-md text-sm font-medium"
              onClick={() => {
                onClose();
                onOpenReturn();
              }}
              icon={<Undo2 className="w-5 h-5" />}
            >
              Returned Bill
            </Button>
            <Button
              key="EXPENSE"
              size="large"
              className="flex flex-col items-center justify-center gap-1.5 h-[72px] text-xs font-semibold tracking-wide border-gray-900 rounded-md text-sm font-medium"
              onClick={() => {
                onClose();
                onOpenExpenses();
              }}
              icon={<ReceiptText className="w-5 h-5" />}
            >
              Expense
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Preferences
            </span>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-gray-400 font-medium">
                  Bill Type
                </span>
                <Select
                  value={billType}
                  onChange={setBillType}
                  className="w-full border-gray-300 rounded-md"
                  size="middle"
                  options={[
                    { value: "pdf_invoice", label: "PDF Invoice" },
                    { value: "thermal_80mm", label: "Thermal Invoice" },
                    { value: "a4_invoice", label: "A4 Invoice" },
                  ]}
                />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-gray-400 font-medium">
                  POS Layout
                </span>
                <Select
                  value={billingLayout}
                  onChange={setBillingLayout}
                  className="w-full border-gray-300 rounded-md"
                  size="middle"
                  options={[
                    { value: "standard", label: "Standard (Grid)" },
                    { value: "compact", label: "Compact (List)" },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </Drawer>

      {/* Customer Addition Modal */}
      <CustomerModal
        open={isCustomerModalOpen}
        onCancel={() => setIsCustomerModalOpen(false)}
        onSuccess={(id, name, credit_limit) => {
          setCustomer({ id, name, credit_limit });
        }}
      />

      {isRestaurant && (
        <TableModal
          open={isTableModalOpen}
          onCancel={() => setIsTableModalOpen(false)}
        />
      )}

      <DraftOptions
        refetchTables={refetchTables}
        ref={draftOptionsRef}
        onCloseQuickAction={onClose}
        onSavePendingChange={setIsSavingDraft}
      />

      <CashFlowModal
        open={isCashFlowOpen}
        onClose={() => setIsCashFlowOpen(false)}
      />
    </>
  );
};
